import type { DateValue } from "@internationalized/date";
import type React from "react";
import { useMemo } from "react";
import type { AnalyticsView } from "@/components/AnalyticsViewConfigurationModal";
import { MetricsLineChartView } from "@/components/MetricsLineChartView";
import { MetricsTableView } from "@/components/MetricsTableView";
import { CURRENCY_METRICS, METRIC_COLORS, METRICS_MAP, PERCENT_METRICS } from "@/constants/metrics";
import { useReportFormatters } from "@/hooks/use-report-formatters";
import { useProcessedMetricsData } from "@/pages/reports/hooks/use-processed-metrics-data";

interface AnalyticsViewDisplayProps {
  view: AnalyticsView;
  timePeriod: "daily" | "weekly" | "monthly";
  dateRange: { start: DateValue; end: DateValue } | null;
  timeGranularity?: "day" | "week" | "month";
  showTitle?: boolean;
}

export const AnalyticsViewDisplay: React.FC<AnalyticsViewDisplayProps> = ({
  view,
  timePeriod,
  dateRange,
  timeGranularity = "week",
  showTitle = false,
}) => {
  // Fetch and transform data
  const { filteredData } = useProcessedMetricsData(view.metrics, timePeriod, dateRange);

  // Build config
  const { gmv: gmvFormatter, video: videoFormatter, percent: percentFormatter } = useReportFormatters();

  const config = useMemo(() => {
    return {
      metrics: view.metrics,
      formatters: Object.fromEntries(
        view.metrics.map((m) => [
          m,
          CURRENCY_METRICS.includes(m)
            ? (v: number) => gmvFormatter.format(v)
            : PERCENT_METRICS.includes(m)
              ? (v: number) => percentFormatter.format(v)
              : (v: number) => videoFormatter.format(v),
        ]),
      ),
      colors: Object.fromEntries(view.metrics.map((m) => [m, METRIC_COLORS[m] || "#6b7280"])),
      labels: Object.fromEntries(view.metrics.map((m) => [m, METRICS_MAP[m]?.label || m])),
    };
  }, [view.metrics, gmvFormatter, videoFormatter, percentFormatter]);

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {showTitle && (
        <div className="px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold text-gray-900">{view.name || "Untitled View"}</h3>
        </div>
      )}
      <div className={showTitle ? "flex-1 min-h-0 px-4 pb-4" : "flex-1 min-h-0 p-4"}>
        {view.layout === "chart" ? (
          <MetricsLineChartView data={filteredData as any} config={config as any} chartType="area" />
        ) : (
          <MetricsTableView data={filteredData as any} config={config as any} />
        )}
      </div>
    </div>
  );
};
